import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import AudioPlayer from "@/components/AudioPlayer";
import LyricsView from "@/components/LyricsView";
import VinylDisc from "@/components/VinylDisc";
import AudioVisualizer from "@/components/AudioVisualizer";
import EqIndicator from "@/components/EqIndicator";
import VerifiedBadge from "@/components/VerifiedBadge";
import ShareLyricsDialog from "@/components/ShareLyricsDialog";
import { parseLrc, findActiveIndex } from "@/lib/lrc";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  ChevronLeft,
  Loader2,
  Save,
  Edit3,
  X,
  Wand2,
  Sparkles,
  Globe2,
  Lock,
  User,
  ImagePlus,
  Trash2,
  Quote,
} from "lucide-react";
import { toast } from "sonner";

export default function Player() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [song, setSong] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [audioEl, setAudioEl] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);

  const seekTargetRef = useRef(null);
  const audioRef = useRef(null);
  const coverInputRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/songs/${id}`);
      setSong(data);
      setDraft(data.lyrics || "");
    } catch (e) {
      setNotFound(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isOwner =
    !!song && !!user && (song.is_owner || song.uploader_id === user.id);

  const saveLyrics = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/songs/${id}/lyrics`, { lyrics: draft });
      setSong((s) => ({ ...s, ...data }));
      setEditing(false);
      toast.success(data.is_synced ? "Saved · synced lyrics detected" : "Lyrics saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const autoFetch = async () => {
    if (!song) return;
    if (!song.artist || !song.title) {
      toast.error("Need both artist and title");
      return;
    }
    setFetching(true);
    try {
      const { data } = await api.get("/lyrics/fetch", {
        params: { artist: song.artist, title: song.title },
      });
      setDraft(data.lyrics || "");
      setEditing(true);
      toast.success("Lyrics fetched — review and save");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lyrics not found");
    } finally {
      setFetching(false);
    }
  };

  const toggleVisibility = async (next) => {
    setSong((s) => ({ ...s, is_public: next }));
    try {
      const { data } = await api.patch(`/songs/${id}/visibility`, { is_public: next });
      setSong((s) => ({ ...s, ...data }));
      toast.success(next ? "Now public on the feed" : "Set to private");
    } catch (e) {
      setSong((s) => ({ ...s, is_public: !next }));
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update visibility"
      );
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Cover must be an image");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Cover too large (max 6MB)");
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("cover", file);
      const { data } = await api.post(`/songs/${id}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSong((s) => ({ ...s, ...data }));
      toast.success("Cover updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't update cover");
    } finally {
      setUploadingCover(false);
    }
  };

  const removeCover = async () => {
    setUploadingCover(true);
    try {
      const { data } = await api.delete(`/songs/${id}/cover`);
      setSong((s) => ({ ...s, ...data }));
      toast.success("Cover removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove cover");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSeek = (t) => {
    seekTargetRef.current?.(t);
  };

  if (notFound) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">
              // 404
            </div>
            <h2 className="mt-2 font-display font-black text-4xl tracking-tighter">
              Track not available.
            </h2>
            <p className="mt-3 text-white/50">It may be private or has been removed.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button
                onClick={() => navigate("/feed")}
                className="bg-[#FF3B30] hover:bg-[#FF5C53] rounded-sm font-display font-bold uppercase text-xs"
                data-testid="goto-feed-btn"
              >
                Browse feed
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!song) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-white/40" />
        </div>
      </Layout>
    );
  }

  const audioUrl = `${API_BASE}/songs/${song.id}/audio`;
  audioRef.current = audioEl;
  const backTo = isOwner ? "/" : "/feed";
  const backLabel = isOwner ? "Library" : "Feed";
  const parsed = parseLrc(song.lyrics || "");
  const shareIndex = parsed.synced ? findActiveIndex(parsed.lines, currentTime) : -1;

  return (
    <Layout>
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 opacity-[0.08]">
          <AudioVisualizer
            audioRef={audioRef}
            playing={playing}
            variant="wave"
            className="w-full h-full"
          />
        </div>
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-[#FF3B30]/[0.08] to-transparent" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 pt-8 pb-44">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white"
          data-testid="back-link"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> {backLabel}
        </Link>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left: vinyl + meta + edit */}
          <div className="lg:col-span-4 xl:col-span-4 space-y-7">
            <div className="relative">
              <VinylDisc title={song.title} playing={playing} coverSong={song} />
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F0F0F] border border-white/10 backdrop-blur">
                <EqIndicator playing={playing} />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                  {playing ? "now playing" : "paused"}
                </span>
              </div>
              {isOwner && (
                <>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      uploadCover(f);
                    }}
                    data-testid="cover-file-input"
                  />
                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                      className="h-9 w-9 rounded-full bg-black/70 backdrop-blur hover:bg-[#FF3B30] text-white flex items-center justify-center transition-colors border border-white/10"
                      title={song.has_cover ? "Change cover" : "Add cover"}
                      data-testid="player-change-cover-btn"
                    >
                      {uploadingCover ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ImagePlus className="h-4 w-4" />
                      )}
                    </button>
                    {song.has_cover && (
                      <button
                        type="button"
                        onClick={removeCover}
                        disabled={uploadingCover}
                        className="h-9 w-9 rounded-full bg-black/70 backdrop-blur hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center transition-colors border border-white/10"
                        title="Remove cover"
                        data-testid="player-remove-cover-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="pt-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] flex items-center gap-2">
                <Sparkles className="h-3 w-3" />
                {song.is_synced ? "// synced lyrics" : "// static lyrics"}
              </div>
              <h1
                className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]"
                data-testid="song-title"
              >
                {song.title}
              </h1>
              <div
                className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-white/50"
                data-testid="song-artist"
              >
                {song.artist || "unknown artist"}
              </div>
              {song.uploader_name && (
                <div
                  className="mt-4 inline-flex items-center gap-2 border border-white/10 rounded-full px-3 py-1.5 bg-[#0F0F0F]/70 backdrop-blur"
                  data-testid="uploader-chip"
                >
                  <User className="h-3 w-3 text-[#FF3B30]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                    shared by
                  </span>
                  <span
                    className="font-display font-bold text-sm text-white inline-flex items-center gap-1"
                    data-testid="uploader-name"
                  >
                    {song.uploader_name}
                    {song.uploader_verified && <VerifiedBadge size={13} />}
                  </span>
                </div>
              )}
            </div>

            {isOwner && (
              <>
                <div
                  className="flex items-center justify-between gap-3 border border-white/10 rounded-sm px-3 py-2.5 bg-[#0F0F0F]/60"
                  data-testid="player-visibility-row"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {song.is_public ? (
                      <Globe2 className="h-3.5 w-3.5 text-[#FF3B30] shrink-0" />
                    ) : (
                      <Lock className="h-3.5 w-3.5 text-white/40 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div
                        className={`font-mono text-[10px] uppercase tracking-[0.25em] ${
                          song.is_public ? "text-[#FF3B30]" : "text-white/60"
                        }`}
                      >
                        {song.is_public ? "public on feed" : "private"}
                      </div>
                      <div className="font-mono text-[9px] tracking-[0.2em] text-white/35 uppercase mt-0.5">
                        {song.is_public
                          ? "anyone can listen and read"
                          : "only you can listen"}
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={!!song.is_public}
                    onCheckedChange={toggleVisibility}
                    className="data-[state=checked]:bg-[#FF3B30] data-[state=unchecked]:bg-white/15"
                    data-testid="player-visibility-toggle"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {!editing && (
                    <>
                      <Button
                        onClick={() => setEditing(true)}
                        variant="outline"
                        className="bg-transparent border-white/10 text-white hover:bg-[#1A1A1A] hover:text-white rounded-sm font-mono uppercase text-xs tracking-[0.2em]"
                        data-testid="edit-lyrics-btn"
                      >
                        <Edit3 className="h-3.5 w-3.5 mr-2" /> Edit lyrics
                      </Button>
                      <Button
                        onClick={autoFetch}
                        disabled={fetching}
                        variant="outline"
                        className="bg-transparent border-white/10 text-white hover:bg-[#1A1A1A] hover:text-white rounded-sm font-mono uppercase text-xs tracking-[0.2em]"
                        data-testid="fetch-lyrics-btn"
                      >
                        {fetching ? (
                          <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                        ) : (
                          <Wand2 className="h-3.5 w-3.5 mr-2 text-[#FF3B30]" />
                        )}
                        Auto-fetch
                      </Button>
                    </>
                  )}
                  {editing && (
                    <>
                      <Button
                        onClick={saveLyrics}
                        disabled={saving}
                        className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-sm font-display font-bold uppercase text-xs tracking-wide"
                        data-testid="save-lyrics-btn"
                      >
                        {saving ? (
                          <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-3.5 w-3.5 mr-2" />
                        )}
                        Save
                      </Button>
                      <Button
                        onClick={() => {
                          setEditing(false);
                          setDraft(song.lyrics || "");
                        }}
                        variant="outline"
                        className="bg-transparent border-white/10 text-white hover:bg-[#1A1A1A] hover:text-white rounded-sm font-mono uppercase text-xs tracking-[0.2em]"
                        data-testid="cancel-edit-btn"
                      >
                        <X className="h-3.5 w-3.5 mr-2" /> Cancel
                      </Button>
                    </>
                  )}
                </div>

                {editing && (
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={`Paste lyrics or .lrc here.\n[00:12.34]First line\n[00:16.10]Next line`}
                    className="bg-transparent border-white/10 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm min-h-[260px] font-mono text-sm"
                    data-testid="lyrics-edit-area"
                  />
                )}
              </>
            )}
          </div>

          <div className="lg:col-span-8 xl:col-span-8">
            <div className="relative h-[72vh] rounded-md overflow-hidden border border-white/5 bg-gradient-to-b from-white/[0.02] to-transparent">
              <div className="absolute top-0 left-0 right-0 h-10 pointer-events-none opacity-50">
                <AudioVisualizer
                  audioRef={audioRef}
                  playing={playing}
                  variant="bars"
                  className="w-full h-full"
                />
              </div>
              {!editing && song.lyrics && (
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="absolute top-3 right-3 z-20 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black/70 backdrop-blur border border-white/10 hover:border-[#FF3B30] hover:bg-[#FF3B30] text-white font-mono text-[10px] uppercase tracking-[0.25em] transition-colors"
                  data-testid="open-share-lyrics-btn"
                >
                  <Quote className="h-3.5 w-3.5" /> Share lyrics
                </button>
              )}
              <LyricsView
                lyrics={editing ? draft : song.lyrics}
                currentTime={currentTime}
                onSeek={handleSeek}
              />
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0A0A0A] to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      <ShareLyricsDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        song={song}
        initialIndex={shareIndex}
      />

      <AudioPlayer
        src={audioUrl}
        title={song.title}
        artist={song.artist}
        song={song}
        autoPlay
        onTimeUpdate={setCurrentTime}
        onPlayingChange={setPlaying}
        onAudioElement={setAudioEl}
        seekRef={seekTargetRef}
      />
    </Layout>
  );
}
